import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { ArrowLeft, Plus, Search, Scale, Edit2, Trash2, AlertTriangle } from 'lucide-react';
import { useUnitStore } from '../../hooks/useUnits';
import { Button, Card, CardContent, useToast } from '@lancar-pos/ui';

export function UnitsPage() {
  const navigate = useNavigate();
  const { units, deleteUnit } = useUnitStore();
  const toast = useToast();
  
  const [searchQuery, setSearchQuery] = useState('');
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [unitToDelete, setUnitToDelete] = useState<typeof units[0] | null>(null);

  const filteredUnits = units.filter((u) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      u.name.toLowerCase().includes(query) ||
      u.abbreviation.toLowerCase().includes(query)
    );
  });

  const handleDelete = (unit: typeof units[0]) => {
    setUnitToDelete(unit);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (!unitToDelete) return;
    
    setDeletingId(unitToDelete.id);
    try {
      await deleteUnit(unitToDelete.id);
      toast.success('Satuan dihapus');
    } catch (error) {
      toast.error('Gagal menghapus satuan');
    } finally {
      setDeletingId(null);
      setShowDeleteDialog(false);
      setUnitToDelete(null);
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'count': return 'Jumlah';
      case 'weight': return 'Berat';
      case 'length': return 'Panjang';
      case 'volume': return 'Volume';
      default: return type;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => window.history.back()}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <h1 className="text-xl font-bold text-gray-900">Satuan Produk</h1>
          </div>
          <Button onClick={() => window.location.href = '/app/inventory/units/new'}>
            <Plus className="w-4 h-4 mr-2" />
            Tambah Satuan
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-4 lg:p-6 space-y-4">
        <Card>
          <CardContent className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Cari satuan..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Nama
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Singkat
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Tipe
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                      Default
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                      Aksi
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredUnits.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                        <Scale className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                        <p>Belum ada satuan</p>
                        <p className="text-sm text-gray-400">
                          {searchQuery ? 'Coba kata kunci lain' : 'Tambah satuan pertama Anda'}
                        </p>
                      </td>
                    </tr>
                  ) : (
                    filteredUnits.map((unit) => (
                      <tr key={unit.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <span className="font-medium text-gray-900">{unit.name}</span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono">
                          {unit.abbreviation}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {getTypeLabel(unit.type)}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {unit.isDefault && (
                            <span className="px-2 py-1 text-xs font-medium bg-primary-100 text-primary-700 rounded">
                              Default
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-center gap-2">
                            <button
                              onClick={() => navigate({ to: '/app/inventory/units/$unitId', params: { unitId: unit.id } })}
                              className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              title="Edit"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(unit)}
                              disabled={deletingId === unit.id}
                              className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                              title="Hapus"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </main>

      {showDeleteDialog && unitToDelete && (
        <>
          <div className="fixed inset-0 bg-black/50 z-40" onClick={() => setShowDeleteDialog(false)} />
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-xl shadow-xl z-50 w-full max-w-sm p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900">Hapus Satuan?</h2>
            </div>
            <p className="text-gray-600 mb-6">
              Satuan "{unitToDelete.name}" akan dihapus.
            </p>
            <div className="flex gap-3 justify-end">
              <Button variant="outline" onClick={() => setShowDeleteDialog(false)} disabled={deletingId === unitToDelete.id}>
                Batal
              </Button>
              <Button variant="danger" onClick={confirmDelete} loading={deletingId === unitToDelete.id}>
                Ya, Hapus
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
